import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5e9', '#84cc16', '#f97316'];

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatCompactMoney = (value, currency, locale) =>
  new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const InventoryValueByCategoryWidget = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.inventoryValueByPartCategory || [];
    return rows.map((row, index) => ({
      category: row.category,
      value: Number(row.totalValue) || 0,
      color: PALETTE[index % PALETTE.length],
    }));
  }, [data]);

  const isEmpty = chartData.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.inventory_value_by_category', 'Inventory Value by Category')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.inventory_value_by_category.empty', 'No inventory value to display.')}
      onRefresh={handleRefresh}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="category" tick={{ fontSize: 12 }} />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => formatCompactMoney(v, currency, i18n.language)}
            width={80}
          />
          <Tooltip
            formatter={(value) => [formatMoney(value, currency), t('shared.value', 'Value')]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default InventoryValueByCategoryWidget;
